type AggregatedRule = {
  rule: string;
  dates: string[];
  count: number;
  language: string;
};

/**
 * Aggregates rule data from an array of entries.
 * @param data - Array of objects containing rule data.
 * @returns An array of AggregatedRule objects with accumulated dates and counts per rule.
 */
export const aggregateRulesData = (data: any[]): AggregatedRule[] => {
  const aggregatedData: { [ruleKey: string]: AggregatedRule } = {};

  // Validate that input is an array
  if (!Array.isArray(data)) {
    console.error('Invalid input: Expected an array.');
    return []; // Return an empty array on invalid input
  }
  
  data.forEach((entry, index) => {
    const { date, language, ...rules } = entry;

    // Verify if date exists and is a valid ISO date string
    if (!date || isNaN(Date.parse(date))) {
      console.warn(`Skipping entry at index ${index}: Invalid or missing date "${date}"`);
      return; // Skip this entry if the date is invalid
    }

    // Format the date to YYYY-MM-DD HH:MM for simplicity
    const formattedDate = new Date(date).toISOString().slice(0, 16).replace('T', ' '); // YYYY-MM-DD HH:MM

    // Iterate through each rule (e.g., rule1, rule2)
    Object.keys(rules).forEach((ruleKey) => {
      const ruleValue = rules[ruleKey];

      // Ensure ruleValue is a valid number and ruleKey starts with 'rule'
      if (typeof ruleValue !== 'number' || ruleValue <= 0 || !ruleKey.startsWith('rule')) {
        console.warn(
          `Skipping invalid rule entry at index ${index}: ruleKey="${ruleKey}", ruleValue="${ruleValue}"`
        );
        return; // Skip invalid rules
      }

      // Initialize the rule entry if it doesn't exist in aggregatedData
      if (!aggregatedData[ruleKey]) {
        aggregatedData[ruleKey] = {
          rule: ruleKey,
          dates: [],
          count: 0,
          language: typeof language === 'string' && language.trim() ? language : 'fr', // Fallback to 'fr'
        };
      }

      // Add the formatted date to the rule's dates array
      aggregatedData[ruleKey].dates.push(formattedDate);

      // Increment the count by the rule value
      aggregatedData[ruleKey].count += ruleValue;
    });
  });

  // Convert the aggregatedData object to an array
  return Object.values(aggregatedData);
};
